import { Grid, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Field, Formik, FormikErrors } from 'formik';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroCheckbox,
	CerebroDrawer,
	CerebroForm,
	CerebroHeading,
	CerebroHierarchyFolderTree,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroStyledBox,
	CerebroText,
} from '../../shared/components';
import { useApi } from '../../shared/context';
import { QueryKeys, useDataRoomFolderHierarchyQuery } from '../../shared/hooks';
import { FormikUtils } from '../../shared/utils';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getApiResponseData } from '../../shared/utils/axios';
import { DataRoomFolder } from '../../shared/models';

interface MoveFolderDocumentsDrawerProps {
	dataRoomGuid: string | undefined;
	folder: DataRoomFolder | null | undefined;
	close: () => void;
}

export const MoveFolderDocumentsDrawer: React.FC<MoveFolderDocumentsDrawerProps> = ({
	dataRoomGuid,
	folder,
	close,
}) => {
	const api = useApi();
	const client = useQueryClient();
	const snackbar = useSnackbar();
	const foldersQuery = useDataRoomFolderHierarchyQuery(dataRoomGuid);

	const validateForm = (values: MoveDocumentFormValues): MoveDocumentFormValueErrors => {
		const errors: MoveDocumentFormValueErrors = {};

		if (!values.fileGuid || values.fileGuid.length == 0) {
			errors.fileGuid = ['Requires at least one selected file to move.'];
		}

		if (!values.folderGuid) {
			errors.folderGuid = 'Data room folder is required';
		} else {
			if (values.folderGuid == folder?.guid) {
				errors.folderGuid = 'Must select a different folder.';
			}
		}

		return errors;
	};

	const moveDocumentMutation = useMutation(
		async ({ values }: MoveDocumentFormMutationVariables) => {
			const folderPatch = {
				folderGuid: values.folderGuid,
				fileGuid: values.fileGuid,
			};

			const { data, error } = await getApiResponseData<void>(
				api.post(CerebroApiEndpoints.Documents.FolderFileMove(dataRoomGuid, folder?.guid), folderPatch)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.Documents.Root(dataRoomGuid, folder?.guid));
				snackbar.enqueueSnackbar('Data room documents were moved.', { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={folder != null} onClose={() => close()} PaperProps={{ sx: { width: '700px' } }}>
			<CerebroHeading>{folder?.name}</CerebroHeading>
			<Formik
				initialValues={
					{
						folderGuid: folder?.folderGuid,
						fileGuid: [] as string[],
					} as MoveDocumentFormValues
				}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(moveDocumentMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values, setFieldValue }) => {
					return (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={11}>
									<CerebroText>
										Only files visible to all users can be moved, any files that are private with
										selected users are excluded from this list.
									</CerebroText>
									<TableContainer>
										<Table>
											<TableHead>
												<TableRow>
													<TableCell>Select</TableCell>
													<TableCell>Name</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{folder?.files
													.filter(f => f.isVisibleToAll)
													.map(file => (
														<TableRow key={file.guid}>
															<TableCell>
																<Field
																	key={file.guid}
																	as={CerebroCheckbox}
																	name={file.guid}
																	disabled={isSubmitting}
																	error={
																		FormikUtils.IsInvalid(
																			'fileGuid',
																			errors,
																			touched
																		) &&
																		errors.fileGuid &&
																		errors.fileGuid.length > 0
																	}
																	checked={values.fileGuid.some(w => w == file.guid)}
																	onChange={(e: any) => {
																		if (e.target.checked) {
																			values.fileGuid.push(e.target.name);
																			setFieldValue('fileGuid', values.fileGuid);
																		} else {
																			const id = values.fileGuid.indexOf(
																				e.target.name
																			);
																			if (id > -1) {
																				values.fileGuid.splice(id, 1);
																				setFieldValue(
																					'fileGuid',
																					values.fileGuid
																				);
																			}
																		}
																	}}
																/>
															</TableCell>
															<TableCell>{file.name}</TableCell>
														</TableRow>
													))}
											</TableBody>
										</Table>
									</TableContainer>
									{FormikUtils.IsInvalid('fileGuid', errors, touched) &&
										errors.fileGuid &&
										errors.fileGuid.length > 0 && (
											<CerebroText variant="error">{errors.fileGuid[0]}</CerebroText>
										)}
								</Grid>

								<Grid item xs={8}>
									<CerebroLabel>Data Room Folder</CerebroLabel>
									{foldersQuery.isLoading && <Skeleton height={60} />}
									{foldersQuery.isSuccess && (
										<CerebroStyledBox alignitems="left" sx={{ marginTop: 1 }}>
											<CerebroHierarchyFolderTree
												folder={foldersQuery.data}
												selectedGuid={values.folderGuid}
												hidePrivateFolders={false}
												onSelected={value => {
													values.folderGuid = value;
													setFieldValue('folderGuid', value);
												}}
											/>
										</CerebroStyledBox>
									)}
									{FormikUtils.IsInvalid('folderGuid', errors, touched) && (
										<CerebroText variant="error">{errors.folderGuid}</CerebroText>
									)}
								</Grid>

								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type MoveDocumentFormMutationVariables = FormikUtils.MutateOnSubmitVariables<MoveDocumentFormValues>;
interface MoveDocumentFormValues {
	folderGuid: string | undefined;
	fileGuid: string[];
}
type MoveDocumentFormValueErrors = FormikErrors<MoveDocumentFormValues>;
